import { getSeriesWindowing } from "../../utils/dicomVoi";
/**
 * ProtocolViewport - Enhanced Viewport Wrapper for Hanging Protocol
 *
 * Wraps VolumeViewport with additional overlays for the hanging protocol:
 * - ViewportOverlay (four corners info)
 * - OrientationMarkers (A/P/L/R labels)
 */

import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import VolumeViewport from "../VolumeViewport";
import SessionVolumeMount from "../viewport/SessionVolumeMount";
import StackViewport from "../StackViewport";
import { decideViewport } from "../../utils/pickViewportComponent";
import { gpuVerdictAtom } from "../../atoms/gpuVerdictAtom";
import { ViewportOverlay, SeriesInfo, ViewportMetrics } from "./ViewportOverlay";
import { OrientationMarkers } from "./OrientationMarkers";
import {
  applyRadiologicalFlip,
  deriveOrientationLabels,
  paneNeedsRadiologicalFlip,
  paneOrientationIop,
} from "@/viewport/radiologicalOrientation";
import { EmptySlotPlaceholder } from "./EmptySlotPlaceholder";
import { reformatSeriesLabel } from "./reformatSeriesLabel";
import { ViewportContextMenu } from "./ViewportContextMenu";
import { resolveNewViewportFlag } from "./resolveNewViewportFlag";
import type { ResliceTarget } from "./resolveGridReslice";
import { VolumeLoadingPlaceholder } from "./VolumeLoadingPlaceholder";
import { SliceNavigator } from "./SliceNavigator";
import { BumpRadiusControl } from "../labeling/BumpRadiusControl";
import { SegmentationStatsOverlay } from "./SegmentationStatsOverlay";
import { ViewportMinimap } from "./ViewportMinimap";
import { ViewportModeBadge } from "../viewport/ViewportModeBadge";
import {
  StudyMetadataResponse,
  SeriesMetadata,
  buildS3ImageIds,
  shouldAutoInvert,
} from "../../utils/dicomMetadataLoader";
import { resolveAiJumpCenter } from "../aiJumpWorld";
import { warnOnMixedSignedTransferSyntax } from "../../utils/signCorrectionTelemetry";
import { SlotId, getSlotConfigs } from "../../utils/hangingProtocolService";
import { derivePlane, reformatBlockerFor } from "../../utils/seriesClassification";
import { type Vec3 } from "../../utils/anatomicScroll";
import { resolveListAnatomicSign } from "./protocolViewport.listAnatomicSign";
import type { CornerstoneMetaDataLike } from "@/components/volumeViewport.sliceWorldPosition";
import { useSlotState } from "../../hooks/useSlotState";
import { useDisplayedSource } from "../../hooks/useDisplayedSource";
import { useAtomValue, useSetAtom } from "jotai";
import {
  volumeStatusFamily,
  maximizedSlotAtom,
  toggleMaximizeAtom,
  prioritySeriesIdAtom,
} from "../../atoms/hangingProtocolAtoms";
import { activeMeasurementToolAtom } from "../../atoms/measurementAtoms";
import {
  lockedSeriesIdAtom,
  activeSegmentIndexAtom,
  isSegmentationActiveAtom,
  nnSegmentationResultAtom,
} from "../../atoms/segmentationAtoms";
import { annotationBBoxesAtom, type AnnotationBBox } from "../../atoms/projectionAtoms";
import { segmentationMarksAtom } from "../../atoms/segmentationMarksAtom";
import { aiJumpTargetAtom } from "../../atoms/positionAtoms";
import { showsFindingPulse } from "./findingPulse";
import { computeContourBBox, mergeBBoxes } from "../../utils/projectionUtils";
import { on as busOn, off as busOff } from "../../lib/viewerBus";
import { activeSeriesDragIdAtom, seriesDragHoverSlotAtom } from "../../atoms/seriesDragAtoms";
import { projectGatingModeAtom } from "../../atoms/gatingAtoms";
import { GATING_MODE, PHASE_LABEL_SHORT } from "../../types/gating";
import { classifySeriesPhase } from "../../utils/phaseClassifier";
import {
  getCornerstone,
  getCornerstoneTools,
  findHangingProtocolViewport,
} from "../../utils/dicomLoader";
import { FaSpinner } from "../../utils/icons";
import { logger } from "@/utils/logger";

/** Cornerstone's CAMERA_MODIFIED event name (CORE, dispatched on the viewport
 *  element). Not bubbling — see the capture-phase listener below. */
const CORNERSTONE_CAMERA_MODIFIED = "CORNERSTONE_CAMERA_MODIFIED";

export interface ProtocolViewportProps {
  slotId: SlotId;
  seriesId: string | null;
  seriesMetadata: SeriesMetadata | null;
  studyMetadata: StudyMetadataResponse;
  studyId: string;
  onSeriesLoadComplete?: (seriesId: string, totalFrames: number, slotId: SlotId) => void;
  /** Use segmentation slot labels instead of contrast-based labels */
  isSegmentationProject?: boolean;
  blindAnatomyTask?: boolean;
  /** Called before assigning a dropped series — returns true if assignment should proceed */
  onBeforeSeriesDrop?: (droppedSeriesId: string, slotId: SlotId) => boolean;
  /** Called to handle the actual series drop — grid coordinates swap logic */
  onSeriesDrop?: (droppedSeriesId: string, targetSlotId: SlotId) => void;
  /** When set, this slot renders `seriesId` (the source axial the grid passes)
   *  resliced to `reslice.plane` — an MPR companion. View + measurements only;
   *  segmentation is disabled on the pane. */
  reslice?: ResliceTarget;
  /** Authoritative unified-path flag from the grid, so this pane and the grid's
   *  shared workspace can never disagree. Falls back to URL-param resolution
   *  when omitted. */
  unifiedPath?: boolean;
}

export const ProtocolViewport: React.FC<ProtocolViewportProps> = ({
  slotId,
  seriesId,
  seriesMetadata,
  studyMetadata,
  studyId,
  onSeriesLoadComplete,
  isSegmentationProject = false,
  blindAnatomyTask = false,
  onBeforeSeriesDrop,
  onSeriesDrop,
  reslice,
  unifiedPath,
}) => {
  // Use consolidated slot state hook (atomFamily-based, per-slot isolation)
  const {
    sliceIndex,
    totalSlices,
    windowing,
    zoom,
    loadingState,
    isActive,
    isInverted,
    pendingJump,
    listAnatomicSign,
    setSliceIndex,
    setWindowing,
    setActive,
    setTotalSlices,
    setListAnatomicSign,
    setZoom,
    setPan,
    assignSeries,
    setLoadingState,
    promotePendingJump,
  } = useSlotState(slotId);

  // Unified viewport path (#575), staged rollout — resolved through the shared
  // `resolveNewViewportFlag` so this pane and HangingProtocolGrid's workspace
  // never disagree. See that helper for the rollout/override semantics.
  const newViewportFlag = useMemo(
    () => blindAnatomyTask || (unifiedPath ?? resolveNewViewportFlag(isSegmentationProject)),
    [blindAnatomyTask, unifiedPath, isSegmentationProject],
  );

  const maximizedSlot = useAtomValue(maximizedSlotAtom);
  const toggleMaximize = useSetAtom(toggleMaximizeAtom);
  const activeMeasurementTool = useAtomValue(activeMeasurementToolAtom);
  const lockedSeriesId = useAtomValue(lockedSeriesIdAtom);
  const isLockedViewport = lockedSeriesId != null && lockedSeriesId === seriesId;
  const segmentationActive = useAtomValue(isSegmentationActiveAtom);
  const isSegmentationActive = !blindAnatomyTask && segmentationActive;
  const activeSegmentIndex = useAtomValue(activeSegmentIndexAtom);
  const setAnnotationBBoxes = useSetAtom(annotationBBoxesAtom);
  // nnCrossViewportSync: last positions received from the bus (null = no sync yet).
  const [nnSyncPositions, setNnSyncPositions] = useState<{
    axial: number;
    coronal: number;
    sagittal: number;
  } | null>(null);
  useEffect(() => {
    const handler = busOn("nnCrossViewportSync", (positions) => {
      setNnSyncPositions(positions);
    });
    return () => busOff("nnCrossViewportSync", handler);
  }, []);
  const nnSegResult = useAtomValue(nnSegmentationResultAtom);
  const has3DViewer = !!(
    nnSegResult?.planeContours?.coronal || nnSegResult?.planeContours?.sagittal
  );
  // Compute the projected slice index for the orange scrollbar marker.
  // Maps the normalized sync position to this viewport's slice range.
  const projectedSlice = (() => {
    if (!nnSyncPositions || totalSlices <= 1) return null;
    let normalized: number | null = null;
    if (slotId === "top-left") normalized = nnSyncPositions.axial; // axial slot (#216)
    else if (slotId === "top-right") normalized = nnSyncPositions.coronal; // coronal slot
    else if (slotId === "bottom-left") normalized = nnSyncPositions.sagittal; // sagittal slot
    if (normalized == null) return null;
    return Math.round(normalized * (totalSlices - 1));
  })();
  const isZoomMode = activeMeasurementTool === "zoom";
  const isPanMode = activeMeasurementTool === "pan";

  // Top-right phase badge — classifies this slot's own series so each viewport
  // shows its own phase, not the globally-active one. Falls back behind any
  // higher-priority top-right badge (e.g. zoom mode).
  const gatingMode = useAtomValue(projectGatingModeAtom);
  const gpuVerdict = useAtomValue(gpuVerdictAtom);
  const phaseLabel = useMemo(() => {
    if (gatingMode !== GATING_MODE.PHASE_DEPENDENT) return null;
    const description = seriesMetadata?.series_description ?? null;
    const phase = classifySeriesPhase(description, null);
    // Coverage counter — without this, a broken heuristic looks identical to
    // one that never triggered (CLAUDE.md root rule).
    logger.info(
      "phase_classifier_coverage feature=protocol_viewport_badge cohort=%s study=%s candidates=%d empty=%d description=%s",
      gatingMode,
      studyId,
      1,
      phase ? 0 : 1,
      description ?? "",
    );
    return phase ? PHASE_LABEL_SHORT[phase] : null;
  }, [gatingMode, seriesMetadata?.series_description, studyId]);

  // Track which slices have contour annotations (for purple ticks on scrollbar)
  const [annotatedSlices, setAnnotatedSlices] = useState<number[]>([]);

  // Volume readiness — referenced by both the tick recompute trigger here and
  // the resetSlotView listener below. Declared once at this position so the
  // tick effect can depend on it (sliceIndex change alone doesn't fire when a
  // case loads with saved annotations and the user doesn't scroll).
  const volumeStatus = useAtomValue(volumeStatusFamily(seriesId));

  // Clear projection bboxes when segmentation ends (only from the viewport that was locked)
  useEffect(() => {
    if (!isSegmentationActive) {
      setAnnotationBBoxes([]);
    }
  }, [isSegmentationActive, setAnnotationBBoxes]);

  const computeAnnotatedSlices = useCallback(() => {
    // Drop the `isLockedViewport` gate so companion viewports (different plane
    // than the drawn one) also paint scrollbar ticks. Cross-plane slice
    // indices are derived from polyline world points via getSliceViewInfo +
    // imageData.worldToIndex — see below.
    if (!isSegmentationActive) {
      setAnnotatedSlices([]);
      return;
    }
    try {
      const cornerstone = getCornerstone();
      const csTools = getCornerstoneTools();
      const viewportPrefix = `hangingProtocol_${slotId}`;

      // Find viewport
      const engines: any[] = cornerstone.getRenderingEngines?.() ?? [];
      let viewport: any = null;
      for (const engine of engines) {
        for (const vp of engine.getViewports?.() ?? []) {
          if (vp.id?.startsWith(viewportPrefix)) {
            viewport = vp;
            break;
          }
        }
        if (viewport) break;
      }
      if (!viewport) return;

      const imageIds: string[] = viewport.getImageIds?.() ?? [];
      // Tick lookup goes through `imageIds.indexOf(referencedImageId)` — returns
      // the same list-index frame the SliceNavigator thumb sits in (per
      // VolumeViewport.tsx → kStepToListIndex → setCurrentSlice). Using
      // worldToIndex(polyline)[2] would give the volume-K step-index, which on
      // reversed-HTJ2K canonicalized volumes differs from list-index by N-1-i —
      // ticks would render mirrored across the midpoint.
      //
      // The bbox push is intentionally *not* gated on same-series membership:
      // cross-view projection collects every contour's z-extent regardless of
      // which series the annotation lives on, so companion-plane propagated
      // contours still contribute to the merged projection bbox on the locked
      // viewport.

      const allAnns: any[] = csTools.annotation.state.getAllAnnotations() ?? [];
      const sliceSet = new Set<number>();
      const bboxes: AnnotationBBox[] = [];
      for (const ann of allAnns) {
        if (
          ann.metadata?.toolName !== "PlanarFreehandContourSegmentationTool" ||
          ann.data?.segmentation?.segmentIndex !== activeSegmentIndex ||
          !ann.metadata?.referencedImageId
        ) {
          continue;
        }

        // Slice ticks: same-series only. Cross-series annotations get -1 from
        // indexOf and skip — their ticks belong to their own viewport.
        const listIdx = imageIds.indexOf(ann.metadata.referencedImageId);
        if (listIdx >= 0) sliceSet.add(listIdx);

        // Bbox: every same-segment contour contributes to the cross-view
        // projection regardless of source series.
        const polyline = ann.data?.contour?.polyline;
        if (polyline && polyline.length >= 3) {
          const bbox = computeContourBBox(polyline);
          if (bbox) bboxes.push({ bbox, segmentIndex: activeSegmentIndex });
        }
      }
      setAnnotatedSlices(Array.from(sliceSet).sort((a, b) => a - b));

      // The bbox atom is global; only the locked viewport (annotation owner)
      // should write to it so cross-plane viewports don't clobber each other.
      if (!isLockedViewport) return;

      // Merge per-contour bboxes into a single bbox per annotation group.
      // Expand z-range to cover the full extent of all annotated slices —
      // without this, axial contours (all at ~same z) project as a thin line
      // on coronal/sagittal instead of showing the full z-extent.
      if (bboxes.length > 0) {
        const merged = mergeBBoxes(bboxes.map((b) => b.bbox));
        if (merged) {
          // Get world z-positions of all annotated slices to expand bbox z-range
          const annotatedImageIds = Array.from(sliceSet)
            .map((idx) => imageIds[idx])
            .filter(Boolean);
          let zMin = merged.minZ,
            zMax = merged.maxZ;
          for (const imgId of annotatedImageIds) {
            try {
              const ipm = cornerstone.metaData.get("imagePlaneModule", imgId);
              if (ipm?.imagePositionPatient) {
                const z = ipm.imagePositionPatient[2];
                zMin = Math.min(zMin, z);
                zMax = Math.max(zMax, z);
              }
            } catch (err) {
              logger.debug("[ProtocolViewport] imagePlaneModule lookup skipped:", err);
            }
          }
          // Add half a slice thickness on each side so the projection spans the full slice
          const sliceThickness = seriesMetadata?.slice_thickness ?? 5.0;
          merged.minZ = zMin - sliceThickness / 2;
          merged.maxZ = zMax + sliceThickness / 2;
          setAnnotationBBoxes([{ bbox: merged, segmentIndex: activeSegmentIndex }]);
        } else {
          setAnnotationBBoxes([]);
        }
      } else {
        setAnnotationBBoxes([]);
      }
    } catch (err) {
      logger.warn("protocol_viewport_compute_annotated_slices_failed err=%o", err);
    }
  }, [isSegmentationActive, isLockedViewport, slotId, activeSegmentIndex, setAnnotationBBoxes]);

  // Recompute on slice change + volume-ready. Without the volumeStatus dep,
  // a case that loads with pre-existing saved contours (no user scroll yet)
  // would run computeAnnotatedSlices once before getImageData/imageIds were
  // populated, clear sliceSet, and never re-fire.
  useEffect(() => {
    computeAnnotatedSlices();
  }, [sliceIndex, volumeStatus, computeAnnotatedSlices]);

  // Recompute on annotation events
  useEffect(() => {
    if (!isSegmentationActive) return;
    let target: any = null;
    try {
      target = getCornerstone().eventTarget;
    } catch (e) {
      logger.debug("[ProtocolViewport] eventTarget unavailable:", e);
      return;
    }
    if (!target) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const debounced = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(computeAnnotatedSlices, 250);
    };

    const events = [
      "CORNERSTONE_TOOLS_ANNOTATION_COMPLETED",
      "CORNERSTONE_TOOLS_ANNOTATION_MODIFIED",
      "CORNERSTONE_TOOLS_ANNOTATION_REMOVED",
      "CORNERSTONE_TOOLS_ANNOTATION_INTERPOLATION_PROCESS_COMPLETED",
    ];
    for (const evt of events) target.addEventListener(evt, debounced);
    return () => {
      for (const evt of events) target.removeEventListener(evt, debounced);
      if (timer) clearTimeout(timer);
    };
  }, [isSegmentationActive, computeAnnotatedSlices]);

  // Priority series — used to route slice-nav requests to the AI-predicted viewport.
  const prioritySeriesId = useAtomValue(prioritySeriesIdAtom);
  const isPriority = seriesId !== null && seriesId === prioritySeriesId;

  // Listen for slice navigation requests from SegmentAnnotationPanel.
  // Bus delivers every emit regardless of value — no trigger counter needed.
  useEffect(() => {
    const handler = busOn("sliceNavRequest", ({ slice }) => {
      if (isLockedViewport || isPriority) {
        setSliceIndex(slice);
      }
    });
    return () => busOff("sliceNavRequest", handler);
  }, [isLockedViewport, isPriority, setSliceIndex]);

  // volumeStatus declared near the top of this component (see comment there).

  // Listen for camera reset requests from resetSlotViewAtom. Resets when
  // either (a) the volume is fully loaded (volumeStatus === "ready"), or
  // (b) this slot is a stack pane — stack viewports never reach
  // volumeStatus="ready" because they don't go through the volume-loading
  // pipeline at all (they emit one "displayable" event in setStack and
  // call it done). Without the stack-pane carve-out, the reset-view
  // affordance silently no-ops on non-reconstructable series. The
  // imperative `vp.resetCamera(...)` works on any Cornerstone viewport
  // type, so the carve-out is safe.
  //
  // Validate seriesId against current study's metadata before deciding the
  // viewport kind — global Jotai atoms may briefly hold series IDs from the
  // previous study during navigation. A single shared `viewportDecision`
  // means every consumer (resetSlotView gate, VolumeViewport vs
  // StackViewport render, stackSegEnabled below) agrees on stack vs volume.
  // Two separate `decideViewport` calls — one raw, one validated — would
  // disagree mid-validation and produce inconsistent gating.
  const validatedSeriesId = useMemo(() => {
    if (!seriesId) return null;
    if (!studyMetadata.series?.[seriesId]) return null;
    return seriesId;
  }, [seriesId, studyMetadata.series]);
  const validatedSeriesMetadata = validatedSeriesId ? seriesMetadata : null;

  // AI segmentation marks for THIS slot's series. Marks can span a concatenated
  // multi-series stack, so each viewport filters to its own series and the
  // per-series split needs no extra plumbing. Suppressed under `reslice`: a
  // reformatted plane has its own index space, so a native-acquisition slice
  // index would tick the wrong row.
  const segmentationMarkSet = useAtomValue(segmentationMarksAtom);
  const seriesSegmentationMarks = useMemo(() => {
    if (blindAnatomyTask || !segmentationMarkSet || !validatedSeriesId || reslice) return [];
    return segmentationMarkSet.marks.filter((m) => m.seriesId === validatedSeriesId);
  }, [blindAnatomyTask, segmentationMarkSet, validatedSeriesId, reslice]);
  const viewportDecision = useMemo(
    () => decideViewport(validatedSeriesMetadata, gpuVerdict),
    [validatedSeriesMetadata, gpuVerdict],
  );
  const isStackPane = viewportDecision.kind === "stack";

  // #coronal-lr: what this pane's geometry is and whether it must be flipped to
  // radiological. Declared here (rather than beside the markers below) because
  // the resetSlotView handler needs it — `resetCamera` clears flipHorizontal.
  const paneOrientation = useMemo(
    () => ({
      nativeIop: validatedSeriesMetadata?.image_orientation_patient ?? null,
      nativePlane: validatedSeriesMetadata ? derivePlane(validatedSeriesMetadata) : null,
      reslicePlane: reslice?.plane,
    }),
    [validatedSeriesMetadata, reslice?.plane],
  );
  const paneFlipRequired = paneNeedsRadiologicalFlip(paneOrientation);

  useEffect(() => {
    const handler = busOn("resetSlotView", ({ slotId: targetSlotId }) => {
      if (targetSlotId !== slotId) return;
      // The new-viewport (?newViewport=1) path drives `onLoadingStateChange`
      // (displayable) but not `volumeStatusFamily` → "ready", so gate-carve it
      // alongside the stack-pane carve-out (resetCamera works on any enabled
      // Cornerstone viewport regardless of how its load-status atom is driven).
      if (volumeStatus !== "ready" && !isStackPane && !newViewportFlag) return;
      try {
        const vp = findHangingProtocolViewport(slotId);
        if (!vp) return;
        (vp as any).resetCamera({ resetZoom: true, resetPan: true, resetToCenter: false });
        // #coronal-lr: resetCamera unconditionally clears flipHorizontal, so
        // Reset View would otherwise silently reintroduce the mirror on a series
        // stored with a patient-right row cosine. Re-assert it.
        applyRadiologicalFlip(vp, paneFlipRequired, `resetSlotView slot=${slotId}`);
        const cornerstone = getCornerstone();
        const engine = cornerstone
          .getRenderingEngines?.()
          ?.find?.((e: any) =>
            (e.getViewports?.() ?? []).some((v: any) =>
              v.id?.startsWith(`hangingProtocol_${slotId}`),
            ),
          );
        engine?.render?.();
      } catch (err) {
        logger.debug("[ProtocolViewport] resetCamera skipped:", err);
      }
    });
    return () => busOff("resetSlotView", handler);
  }, [slotId, volumeStatus, isStackPane, newViewportFlag, paneFlipRequired]);

  // #coronal-lr: the viewport's LIVE flipHorizontal, the truth the orientation
  // letters are drawn from. Cornerstone fires CAMERA_MODIFIED on the viewport
  // element for every camera write — the load-time flip, Reset View, and the
  // sidebar's manual flip toggle — so this tracks the pixels no matter which of
  // the four render hosts (legacy volume/stack, unified GPU/CPU strategy) owns
  // the pane. The event does NOT bubble, but a capture-phase listener on this
  // wrapper still sees it, so we neither own nor look up the element.
  // `null` = no camera observed yet ⇒ no letters (see the markers block below).
  const [liveFlipHorizontal, setLiveFlipHorizontal] = useState<boolean | null>(null);
  // STATE, not a plain ref, and deliberately so. The wrapper this attaches to is
  // only rendered by the third return branch below — the `!validatedSeriesId`
  // placeholder (~:859) and the reslice-fallback notice (~:972) return ref-less
  // divs. Slot assignment happens in a parent effect (`HangingProtocolGrid`
  // `initializeSlots`) after the children's first commit, and the slot wrapper is
  // keyed `slotId` so this component never remounts. A `useRef` + `[]`-deps
  // effect therefore ran once against `null` and never again: the listener was
  // never attached on a fresh study load, `liveFlipHorizontal` stayed `null`, and
  // the SAFETY-CRITICAL A/P/L/R overlay silently rendered nothing for every pane.
  // A callback ref re-runs this effect the moment the node actually mounts.
  const [paneNode, setPaneNode] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    // A swapped series (or reslice plane) says nothing about the new pane's
    // camera — go back to "unknown" rather than carrying the previous series'
    // flip onto different pixels. Done here rather than in its own effect: two
    // effects sharing these deps run in declaration order, so a separate reset
    // would clear the seed below every time it re-ran.
    setLiveFlipHorizontal(null);
    if (!paneNode) return;
    const onCameraModified = (evt: Event) => {
      const cam = (evt as CustomEvent<{ camera?: { flipHorizontal?: boolean } }>).detail?.camera;
      if (!cam) return;
      setLiveFlipHorizontal(cam.flipHorizontal === true);
    };
    paneNode.addEventListener(CORNERSTONE_CAMERA_MODIFIED, onCameraModified, true);

    // SEED, don't wait for an event. React runs child effects before parent
    // effects, so the host's load-time flip can land before this listener
    // attaches — and CAMERA_MODIFIED is fire-once, so a missed event means no
    // letters until the user happens to move the camera. Reading the camera
    // directly makes the letters independent of winning that race. Left as
    // `null` when no viewport/camera exists yet; the listener covers that case.
    const cam = findHangingProtocolViewport(slotId)?.getCamera?.();
    if (cam && typeof cam.flipHorizontal === "boolean") {
      setLiveFlipHorizontal(cam.flipHorizontal);
    }

    return () => paneNode.removeEventListener(CORNERSTONE_CAMERA_MODIFIED, onCameraModified, true);
    // `validatedSeriesId`/`reslice?.plane` are deps so the seed re-reads after a
    // series swap resets the state below — otherwise the reset would strand the
    // pane at `null` whenever the new host writes its camera before we re-attach.
  }, [paneNode, slotId, validatedSeriesId, reslice?.plane]);

  // `validatedSeriesId` + `validatedSeriesMetadata` are defined above near
  // `viewportDecision` so a single source of truth gates every consumer.

  // Seed totalSlices from series-level metadata (available immediately —
  // doesn't wait for instance-list SSE streaming) so SliceNavigator can show
  // its scrollbar before the volume finishes loading.
  //
  // NOT for reslice (MPR) panes: their total is the reoriented plane's slice
  // count (getNumberOfSlices, bridged via onSliceChange), which is NOT the
  // source axial series' num_instances — seeding that here would clobber the
  // reslice count back to the axial count (the /axial-count readout bug).
  const metadataInstanceCount =
    validatedSeriesMetadata?.num_instances ??
    (validatedSeriesId ? (studyMetadata.series?.[validatedSeriesId]?.length ?? 0) : 0);
  useEffect(() => {
    if (reslice) return;
    if (metadataInstanceCount > 0 && totalSlices !== metadataInstanceCount) {
      setTotalSlices(metadataInstanceCount);
    }
  }, [reslice, metadataInstanceCount, totalSlices, setTotalSlices]);

  // Compute the slot's anatomic-up direction in list-index space from the IPP
  // at list-index 0 and N-1. Routed through scrollbar, ↑/↓, and the wheel tool
  // so all three inputs agree on which way is anatomically up for any volume.
  //
  // LEGACY-PATH ONLY (`!newViewportFlag`). On the unified path the mount owns
  // this — SessionVolumeMount computes geometry.anatomicSign() and bridges it
  // via `onAnatomicSignChange` on its OWN readiness (mount-ready + post-stream
  // flip), which fires for the stack strategy too. This effect's recompute is
  // gated on `volumeStatus` (a volume-only signal), so on the legacy stack path
  // the corrected sign never landed → reformat scroll inverted (#570). Keeping
  // it here serves the `?newViewport=0` escape-hatch's legacy VolumeViewport /
  // StackViewport render; Phase 5 deletes it with the flag. The two writers are
  // mutually exclusive by the flag, so they never fight over the atom.
  //
  // Source the first/last IPP from Cornerstone's real per-slice imagePlaneModule
  // (keyed by the viewport's imageIds list order), falling back to the sidecar
  // only before Cornerstone has cached the slice — see resolveListAnatomicSign.
  useEffect(() => {
    if (newViewportFlag) return; // unified path: the mount is the sole writer
    if (!validatedSeriesId || !validatedSeriesMetadata) {
      if (listAnatomicSign !== 0) setListAnatomicSign(0);
      return;
    }
    const plane = derivePlane(validatedSeriesMetadata);
    const instances = studyMetadata.series?.[validatedSeriesId];
    if (!plane || !instances || instances.length < 2) {
      if (listAnatomicSign !== 0) setListAnatomicSign(0);
      return;
    }
    let metaData: CornerstoneMetaDataLike | null = null;
    let imageIds: string[] | undefined;
    try {
      metaData = getCornerstone().metaData;
      imageIds = findHangingProtocolViewport(slotId)?.getImageIds?.();
    } catch (err) {
      // Cornerstone not initialized yet — fall back to the sidecar IPP and
      // surface it so the wrong-direction fallback isn't silent. Recomputes
      // when volumeStatus advances (the volume mount initializes Cornerstone).
      metaData = null;
      logger.warn(
        "list_anatomic_sign_cornerstone_unavailable slot=%s seriesId=%s err=%o",
        slotId,
        validatedSeriesId,
        err,
      );
    }
    const sidecarFirst = instances[0]?.image_position_patient as Vec3 | null | undefined;
    const sidecarLast = instances[instances.length - 1]?.image_position_patient as
      | Vec3
      | null
      | undefined;
    const sign = resolveListAnatomicSign(metaData, imageIds, sidecarFirst, sidecarLast, plane);
    if (sign !== listAnatomicSign) setListAnatomicSign(sign);
  }, [
    newViewportFlag,
    validatedSeriesId,
    validatedSeriesMetadata,
    studyMetadata.series,
    slotId,
    volumeStatus,
    listAnatomicSign,
    setListAnatomicSign,
  ]);

  // NOTE: We deliberately do NOT seed initialWindowing from the DICOM W/L.
  // The viewer always opens (and Reset always returns) to the standard
  // soft-tissue window — see DEFAULT_WINDOWING wiring in initializeSlots /
  // INITIALIZE / handleSeriesDrop. Slot state already initializes
  // initialWindowing to DEFAULT_WINDOWING, so there is nothing to re-seed.
  // (Reslice/MPR panes therefore also stay at DEFAULT_WINDOWING, matching the
  // source axial under this policy — the #846 DICOM-W/L seed that lived here
  // was dropped because it would put a reslice pane on a different window than
  // its now-DEFAULT axial.)

  // Drag-and-drop state (local only)
  // Drop-target highlight is driven by the pointer-drag controller's shared
  // state: a series is being dragged AND this slot is the one under the cursor.
  const activeSeriesDragId = useAtomValue(activeSeriesDragIdAtom);
  const seriesDragHoverSlot = useAtomValue(seriesDragHoverSlotAtom);
  const isDragOver = activeSeriesDragId !== null && seriesDragHoverSlot === slotId;

  // Context menu state — tracks position and whether it's open
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);

  // Track mousedown position to distinguish right-click from right-drag (zoom)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 2) {
      mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
    }
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    // Only show context menu if the mouse didn't move much (< 5px) since mousedown.
    // If it moved, the user was dragging to zoom — let Cornerstone handle it.
    const start = mouseDownPosRef.current;
    if (start) {
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.sqrt(dx * dx + dy * dy) > 5) {
        mouseDownPosRef.current = null;
        return;
      }
    }
    mouseDownPosRef.current = null;
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  // Reformat rows on the viewport's own right-click menu — a second entry
  // point to the same request the sidebar thumbnail menu makes. On a reslice
  // (MPR) pane the source series is the right target, not the reformatted
  // pane itself; `validatedSeriesMetadata` is already the source series'
  // metadata on such a pane (the grid passes the source down as this pane's
  // `seriesId`). Segmentation layout never offers this — `resolveGridReslice`
  // refuses to reslice there and the grid's `handleReformatRequest` refuses
  // too, so omitting the prop keeps the UI from offering something guaranteed
  // to be refused.
  const reformatSeriesId = reslice?.sourceSeriesId ?? validatedSeriesId;
  const reformat =
    isSegmentationProject || !reformatSeriesId || !validatedSeriesMetadata
      ? undefined
      : {
          seriesId: reformatSeriesId,
          blocker: reformatBlockerFor(validatedSeriesMetadata),
          sourcePlane: derivePlane(validatedSeriesMetadata),
        };

  // Get slot configuration
  const slotConfig = getSlotConfigs(isSegmentationProject)[slotId];

  const handleSliceChange = useCallback(
    (slice: number, total: number) => {
      // The viewport renders `sliceIndex` directly — every slice change (scroll,
      // Cornerstone writeback, jump landing) writes it. The cross-series jump
      // target lives in `pendingJump` and is promoted by the volume-ready gate
      // below, not here.
      setSliceIndex(slice);
      if (total !== totalSlices) {
        setTotalSlices(total);
      }
    },
    [setSliceIndex, setTotalSlices, totalSlices],
  );

  // Handle window/level changes from the viewport
  const handleWindowChange = useCallback(
    (center: number, width: number) => {
      setWindowing(center, width);
    },
    [setWindowing],
  );

  // Handle viewport click to make active
  const handleViewportClick = useCallback(() => {
    setActive();
  }, [setActive]);

  // Double-click toggles spotlight layout (2×2 ↔ this slot at 75% with others stacked on right).
  // Sidebars stay visible. Skipped while a segmentation draw tool is active.
  // When zoom tool is active: double-click zooms in 2× and centers on the clicked point.
  const isDrawingTool =
    activeMeasurementTool === "lasso" ||
    activeMeasurementTool === "bump" ||
    activeMeasurementTool === "regionGrow";
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isDrawingTool || !validatedSeriesId) return;
      e.stopPropagation();

      // Zoom mode: double-click zooms in 2× and centers on clicked point
      if (isZoomMode) {
        try {
          const vp = findHangingProtocolViewport(slotId);
          if (!vp) return;
          const canvas = vp.element?.querySelector("canvas");
          if (!canvas) return;
          const rect = canvas.getBoundingClientRect();
          const canvasPoint: [number, number] = [e.clientX - rect.left, e.clientY - rect.top];
          const currentZoom = vp.getZoom?.() ?? 1;
          const newZoom = currentZoom * 2;
          // Compute pan so the clicked point ends up at canvas center.
          // Image-space point: imgPt = (canvasClick - canvasCenter - pan) / zoom
          // We want: canvasCenter = imgPt * newZoom + newPan + canvasCenter
          // => newPan = -(canvasClick - canvasCenter - pan) * (newZoom / zoom)
          const canvasCenter: [number, number] = [rect.width / 2, rect.height / 2];
          const currentPan = vp.getPan?.() ?? [0, 0];
          const ratio = newZoom / currentZoom;
          const newPan: [number, number] = [
            -(canvasPoint[0] - canvasCenter[0] - currentPan[0]) * ratio,
            -(canvasPoint[1] - canvasCenter[1] - currentPan[1]) * ratio,
          ];
          vp.setZoom(newZoom);
          vp.setPan(newPan);
          vp.render();
        } catch (err) {
          logger.warn("protocol_viewport_dblclick_zoom_failed slot=%s err=%o", slotId, err);
        }
        return;
      }

      toggleMaximize(slotId);
    },
    [slotId, toggleMaximize, isDrawingTool, validatedSeriesId, isZoomMode],
  );

  // Handle camera changes (zoom + pan)
  const handleCameraChange = useCallback(
    (newZoom: number, newPan: [number, number]) => {
      setZoom(newZoom);
      setPan(newPan);
    },
    [setZoom, setPan],
  );

  // Handle direct slice navigation from SliceNavigator. Slider drag is a
  // user-initiated source — also claim active so `useHangingProtocolSync`
  // treats this slot as the broadcast origin.
  const handleDirectSliceChange = useCallback(
    (newSliceIndex: number) => {
      setActive();
      setSliceIndex(newSliceIndex);
    },
    [setActive, setSliceIndex],
  );

  // Wheel over the viewport is also user-initiated (most common scroll path
  // for radiologists). Claim active on wheel capture so a hover-and-wheel —
  // without first clicking the viewport — still broadcasts through
  // `useHangingProtocolSync` to the other phases.
  const handleWheelCapture = useCallback(() => {
    setActive();
  }, [setActive]);

  // Handle loading state changes from VolumeViewport and update volume status
  const setVolumeStatus = useSetAtom(volumeStatusFamily(validatedSeriesId));
  const [isViewportDisplayable, setIsViewportDisplayable] = useState(false);
  const publishDisplayedSource = useDisplayedSource(slotId, studyId, validatedSeriesId);
  const rawAiJumpTarget = useAtomValue(aiJumpTargetAtom);
  const aiJumpTarget = blindAnatomyTask ? null : rawAiJumpTarget;
  const [findingPulse, setFindingPulse] = useState<{ trigger: number; visible: boolean } | null>(
    null,
  );
  const [findingPulsePoint, setFindingPulsePoint] = useState<[number, number] | null>(null);
  // Resolved center for this pane's aiJumpTarget: a genuine in-slice centroid
  // verbatim, else the target slice's REAL plane IPP from Cornerstone, else
  // the sidecar ipp (#921 — the sidecar value is synthesized on canonical
  // studies and displaced on gantry-tilted series). Null when the target
  // isn't this pane's series. Plain function (not memo) so each effect run
  // sees the freshest Cornerstone metadata cache.
  const resolveAiJumpCenterForPane = (): readonly [number, number, number] | null => {
    if (!aiJumpTarget || aiJumpTarget.seriesId !== validatedSeriesId) return null;
    const instances = validatedSeriesId ? studyMetadata.series?.[validatedSeriesId] : undefined;
    const cdn = studyMetadata.canonical_cdn_base_url;
    const ids = cdn && instances?.length ? buildS3ImageIds(cdn, instances) : [];
    try {
      return resolveAiJumpCenter(aiJumpTarget, getCornerstone().metaData, ids).world;
    } catch (err) {
      // Cornerstone not initialized yet — the sidecar value is the only option.
      logger.debug("[ProtocolViewport] aiJump center resolution skipped:", err);
      return aiJumpTarget.centerWorld;
    }
  };
  useEffect(() => {
    if (!showsFindingPulse(aiJumpTarget, validatedSeriesId)) return;
    setFindingPulse({ trigger: aiJumpTarget.trigger, visible: true });
    const fade = window.setTimeout(
      () => setFindingPulse({ trigger: aiJumpTarget.trigger, visible: false }),
      500,
    );
    const hide = window.setTimeout(() => setFindingPulse(null), 1500);
    return () => {
      window.clearTimeout(fade);
      window.clearTimeout(hide);
    };
  }, [aiJumpTarget?.trigger, aiJumpTarget?.seriesId, aiJumpTarget?.locate, validatedSeriesId]);
  useEffect(() => {
    if (
      !aiJumpTarget?.centerWorld ||
      aiJumpTarget.seriesId !== validatedSeriesId ||
      !isViewportDisplayable
    ) {
      return;
    }
    try {
      // Jump to the in-slice centroid (rather than only the IPP corner). This
      // keeps the locator visible when the reader is zoomed or panned. The
      // point is resolved against the volume's real geometry (#921).
      const center = resolveAiJumpCenterForPane();
      if (!center) return;
      (findHangingProtocolViewport(slotId) as any)?.jumpToWorld?.(center);
    } catch {
      // The viewport can be between volume rebinding and displayable; the
      // normal AI slice-jump effect will retry at bind time.
    }
  }, [
    aiJumpTarget?.trigger,
    aiJumpTarget?.seriesId,
    validatedSeriesId,
    isViewportDisplayable,
    slotId,
  ]);
  useEffect(() => {
    if (!findingPulse || !aiJumpTarget?.centerWorld || !isViewportDisplayable) {
      setFindingPulsePoint(null);
      return;
    }
    let didResetCameraForTarget = false;
    // Resolve once per effect run — the same real-geometry point the jump used
    // (#921), so the locator ring and the camera agree on the target plane.
    const baseWorld = resolveAiJumpCenterForPane() ?? aiJumpTarget.centerWorld;
    const update = () => {
      try {
        const viewport = findHangingProtocolViewport(slotId) as any;
        let worldPoint = baseWorld;
        if (aiJumpTarget.segmentIndex != null) {
          const annotations = getCornerstoneTools().annotation.state.getAllAnnotations?.() ?? [];
          const viewportImageIds = new Set(viewport?.getImageIds?.() ?? []);
          const annotation = annotations.find(
            (candidate: any) =>
              candidate?.data?.segmentation?.segmentIndex === aiJumpTarget.segmentIndex &&
              candidate?.metadata?.sliceIndex === aiJumpTarget.sliceIndex &&
              candidate?.metadata?.referencedImageId &&
              viewportImageIds.has(candidate.metadata.referencedImageId),
          ) as any;
          const points = annotation?.data?.contour?.polyline;
          if (Array.isArray(points) && points.length > 0) {
            worldPoint = points
              .reduce(
                (sum: [number, number, number], point: [number, number, number]) => [
                  sum[0] + point[0],
                  sum[1] + point[1],
                  sum[2] + point[2],
                ],
                [0, 0, 0],
              )
              .map((value: number) => value / points.length) as [number, number, number];
          }
        }
        const canvasPoint = viewport?.worldToCanvas?.(worldPoint);
        const element = viewport?.element as HTMLElement | undefined;
        if (!canvasPoint || !element) return setFindingPulsePoint(null);
        // Cornerstone's worldToCanvas already returns CSS-space coordinates;
        // do not rescale by the backing canvas pixel ratio (that halves the
        // point on Retina displays and is especially obvious when zoomed).
        const x = canvasPoint[0];
        const y = canvasPoint[1];
        if (x < 0 || y < 0 || x > element.clientWidth || y > element.clientHeight) {
          // A finding click must reveal the target even when a prior zoom/pan
          // left it outside the canvas. Restore the standard fitted camera once
          // instead of panning the target into black space; the next projection
          // then places the locator over its true anatomy.
          if (!didResetCameraForTarget && typeof viewport.resetCamera === "function") {
            didResetCameraForTarget = true;
            viewport.resetCamera({ resetZoom: true, resetPan: true, resetToCenter: false });
            applyRadiologicalFlip(viewport, paneFlipRequired, `finding target slot=${slotId}`);
            viewport.render?.();
            return;
          }
          setFindingPulsePoint(null);
          return;
        }
        setFindingPulsePoint([x, y]);
      } catch {
        setFindingPulsePoint(null);
      }
    };
    update();
    const timer = window.setInterval(update, 50);
    return () => window.clearInterval(timer);
  }, [findingPulse, aiJumpTarget, isViewportDisplayable, slotId, paneFlipRequired]);
  const lastLoadCompleteSeriesRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    lastLoadCompleteSeriesRef.current = null;
  }, [validatedSeriesId]);
  const handleLoadingStateChange = useCallback(
    (isLoading: boolean, loadedFrames: number, totalFrames: number, displayable: boolean) => {
      // Ignore completion from pixels that were replaced while loading.
      if (!publishDisplayedSource(displayable)) return;
      setLoadingState(isLoading, loadedFrames, totalFrames);
      setIsViewportDisplayable(displayable);

      // Update volume status based on loading progress
      if (validatedSeriesId && totalFrames > 0) {
        if (loadedFrames === totalFrames) {
          // All frames loaded - mark as ready
          setVolumeStatus("ready");
          if (lastLoadCompleteSeriesRef.current !== validatedSeriesId) {
            lastLoadCompleteSeriesRef.current = validatedSeriesId;
            onSeriesLoadComplete?.(validatedSeriesId, totalFrames, slotId);
          }
        } else if (volumeStatus !== "loading" && volumeStatus !== "ready") {
          // Start loading - mark as loading (only if not already loading/ready)
          setVolumeStatus("loading");
        }
      }
    },
    [
      setLoadingState,
      validatedSeriesId,
      volumeStatus,
      setVolumeStatus,
      onSeriesLoadComplete,
      slotId,
      publishDisplayedSource,
    ],
  );

  // Volume-ready gate: the single site that promotes the cross-series jump
  // target into `sliceIndex` (and clears it) once the viewport can display the
  // new series. Also clears the "Navigating to slice X..." indicator. For the
  // stack path `sliceIndex` is already at the start index (resolveStackStartIndex
  // → onSliceChange), so this is a no-op there; for the volume path it drives
  // the jump on the IPP-missing fallback.
  useEffect(() => {
    if (isViewportDisplayable && pendingJump !== null) {
      promotePendingJump();
    }
  }, [isViewportDisplayable, pendingJump, promotePendingJump]);

  // Assign a series dropped on THIS slot. Driven by the pointer-drag controller
  // (useSeriesPointerDrag) via the seriesPointerDrop bus event below — native
  // HTML5 DnD was replaced because Safari won't initiate a drag on a <div>.
  const processDroppedSeries = useCallback(
    (droppedSeriesId: string) => {
      // Block reassignment while an annotation is in progress — moving the
      // locked series to another slot breaks tool enforcement and UI state.
      if (lockedSeriesId) return;
      // On a reformat pane, `seriesId` (the prop) is the reslice SOURCE, not
      // the slot's own series — so this guard also swallows a drop of the
      // source series onto its own reformat pane. That looks like a bug (the
      // natural way to turn a reformat pane back into that series' native
      // view) and was changed once to compare against the slot's own
      // (null-on-reformat) seriesId instead — DON'T repeat that: it let the
      // drop through, but the target slot's real seriesId is null, so
      // `handleSeriesDrop`'s swap-back has nothing to move into the source
      // slot, and the series ends up assigned to BOTH the reformat pane and
      // its original native pane. That's the duplicate-series-across-panes
      // state `hangingProtocolService.ts` Pass C explicitly forbids (cache
      // conflicts + segmentation registration issues + confuses the
      // annotator) — confirmed live: both panes blanked to overlays-only.
      // The real fix is a "remove reformat" affordance that clears `reslice`
      // on the pane directly, not permitting this drop. Tracked as a
      // follow-up; until then this stays a no-op.
      if (!droppedSeriesId || droppedSeriesId === seriesId) return;
      // Check acquisition group mismatch before assigning
      if (onBeforeSeriesDrop && !onBeforeSeriesDrop(droppedSeriesId, slotId)) {
        return; // Dialog will handle the assignment
      }
      // Delegate to grid for swap-aware assignment
      if (onSeriesDrop) {
        onSeriesDrop(droppedSeriesId, slotId);
      } else {
        // Fallback: direct assignment (no swap)
        const droppedSeriesMeta = studyMetadata.series_metadata?.[droppedSeriesId];
        const newWindowing = droppedSeriesMeta ? getSeriesWindowing(droppedSeriesMeta) : undefined;
        const droppedInstances = studyMetadata.series?.[droppedSeriesId];
        warnOnMixedSignedTransferSyntax(droppedSeriesId, droppedInstances);
        const autoInvert = shouldAutoInvert(droppedInstances);
        assignSeries(droppedSeriesId, newWindowing, autoInvert);
      }
    },
    [
      seriesId,
      assignSeries,
      studyMetadata.series,
      studyMetadata.series_metadata,
      lockedSeriesId,
      onBeforeSeriesDrop,
      onSeriesDrop,
      slotId,
    ],
  );

  // The pointer-drag controller emits seriesPointerDrop to whichever slot is
  // under the cursor at release; the matching slot runs the assignment.
  useEffect(() => {
    const handler = busOn("seriesPointerDrop", ({ seriesId: dropped, slotId: target }) => {
      if (target === slotId) processDroppedSeries(dropped);
    });
    return () => busOff("seriesPointerDrop", handler);
  }, [slotId, processDroppedSeries]);

  // Determine border styling based on state
  const getBorderClass = () => {
    if (isDragOver) return "ring-2 ring-primary ring-dashed";
    // Note: ring/box-shadow is clipped by parent's contain:strict + overflow-hidden.
    // Active viewport indicator is rendered as an absolute overlay div instead.
    // (see activeViewportOverlay below)
    return "";
  };

  // Labeled "reconstruction unavailable" body — used when a requested reformat
  // pane falls back to the stack path (weak GPU), so an impossible reformat says
  // so instead of rendering the source plane under a coronal label.
  const renderReconstructionUnavailable = (reason: string) => (
    <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-center text-muted text-sm px-3">
      <span>{slotConfig.plane} reconstruction unavailable</span>
      <span className="text-xs opacity-70">{reason}</span>
    </div>
  );

  // If no series assigned (or stale seriesId), show placeholder with drop zone
  if (!validatedSeriesId) {
    return (
      <div
        className={`relative w-full h-full ${getBorderClass()}`}
        data-series-drop-slot={slotId}
        onClick={handleViewportClick}
        onDoubleClick={handleDoubleClick}
      >
        <EmptySlotPlaceholder />
        {isDragOver && (
          <div className="absolute inset-0 bg-primary/10 flex items-center justify-center pointer-events-none z-20">
            <div className="bg-bg-2/90 px-4 py-2 rounded-lg text-sm text-signal-text-tint">
              Drop to assign series
            </div>
          </div>
        )}
      </div>
    );
  }

  // Slice thickness is only meaningful as a labelled value on the *acquired*
  // plane — for axial sources, that's the axial viewport. On coronal/sagittal
  // companions the rendered "thickness" is just the in-plane voxel spacing
  // along the view-plane normal, which is not the DICOM SliceThickness
  // attribute and confuses radiologists when it disagrees with the axial.
  // On a reslice pane the rendered plane is the reslice target, not the source
  // axial's native plane — use it so slice-thickness labelling suppresses
  // correctly (thickness is only meaningful on the acquired axial plane).
  const overlayPlane = reslice
    ? reslice.plane
    : validatedSeriesMetadata
      ? derivePlane(validatedSeriesMetadata)
      : null;
  const showSliceThickness = overlayPlane === null || overlayPlane === "Axial";

  // Orientation markers (SAFETY CRITICAL) — always derived from a real IOP,
  // never from an assumed plane→letters map.
  //
  // A reslice (MPR companion) pane is the subtle case: the series' native IOP
  // describes the AXIAL SOURCE, but the pane displays a coronal/sagittal
  // reformat of it, so feeding the native IOP here would print axial letters
  // (A/P) on a coronal view. Cornerstone builds the reformat on canonical
  // patient axes, so `paneOrientationIop` returns those instead.
  //
  // The flip half comes from the viewport's LIVE camera (`liveFlipHorizontal`
  // above), not from re-deriving what we asked for at load. Only the live value
  // survives Reset View and the sidebar's manual flip toggle, and only it knows
  // whether the host rendering this pane applied the flip at all. `null` (no
  // camera seen yet) renders no letters: on a laterality overlay, nothing beats
  // letters that might be mirrored.
  const orientationLabels =
    liveFlipHorizontal === null
      ? null
      : deriveOrientationLabels(paneOrientationIop(paneOrientation), liveFlipHorizontal);

  // Build series info for overlay
  const seriesInfo: SeriesInfo = {
    seriesDescription: reformatSeriesLabel(
      validatedSeriesMetadata?.series_description ?? null,
      reslice?.plane ?? null,
    ),
    seriesNumber: validatedSeriesMetadata?.series_number ?? null,
    imageNumber: totalSlices > 0 ? sliceIndex + 1 : null,
    totalImages: totalSlices,
    sliceThickness: showSliceThickness ? (validatedSeriesMetadata?.slice_thickness ?? null) : null,
    seriesUid: validatedSeriesId, // For debugging
  };

  // Build viewport metrics for overlay
  const viewportMetricsData: ViewportMetrics = {
    zoomFactor: zoom,
    windowCenter: windowing.center,
    windowWidth: windowing.width,
  };

  // Drives volume-only overlays (segmentation stats, MPR-streaming progress
  // bars, volume-load placeholder). Stack series — 2D scouts, X-rays, SCs —
  // have none of those state machines and skip the overlays. Pattern restored
  // from PR #347; unintentionally dropped by #372, re-added here so
  // non-reconstructable series render again (and as the integration point for
  // the upcoming GPU-fallback path in Stage 3).
  //
  // Single source: read from the shared `viewportDecision` memo defined near
  // `isStackPane` above. Previously this line called `decideViewport` a
  // second time, which could disagree with the earlier call during
  // validation.
  const viewportKind = viewportDecision.kind;
  const isReconstructable = viewportKind === "volume";

  // Enable segmentation on a stack pane ONLY for the weak-GPU fallback on a
  // geometrically-sound (reconstructable, un-blocked) series. NOT for
  // is_reconstructable=false or blocker-coded series — region-grow is unsafe
  // there (see project CLAUDE.md "is_reconstructable ≠ can be viewed").
  const stackSegEnabled =
    viewportKind === "stack" &&
    gpuVerdict === "stack-only" &&
    validatedSeriesMetadata?.is_reconstructable !== false &&
    !validatedSeriesMetadata?.reconstruction_blocker;

  // A reslice (MPR companion) pane REQUIRES the GPU volume path: Cpu2dStrategy
  // renders the source axial's imageIds verbatim (it does not reslice), so on
  // the stack fallback (weak-GPU gpuVerdict === "stack-only" or ?force_stack=1)
  // a "coronal"/"sagittal" pane would show axial images mislabeled by the
  // orientation markers + plane bracket. Refuse to render a silently-wrong view:
  // surface the same honest "reconstruction unavailable" placeholder instead.
  if (reslice && viewportKind !== "volume") {
    logger.warn(
      "[ProtocolViewport] reslice pane fell back to stack render (cannot reslice) — showing unavailable. slot=%s plane=%s gpuVerdict=%s",
      slotId,
      reslice.plane,
      gpuVerdict,
    );
    return (
      <div
        data-testid={`viewport-slot-${slotId}`}
        data-series-drop-slot={slotId}
        className={`relative w-full h-full ${getBorderClass()}`}
        onClick={handleViewportClick}
        onDoubleClick={handleDoubleClick}
      >
        {renderReconstructionUnavailable("GPU volume rendering unavailable on this device")}
        {isDragOver && (
          <div className="absolute inset-0 bg-primary/10 flex items-center justify-center pointer-events-none z-20">
            <div className="bg-bg-2/90 px-4 py-2 rounded-lg text-sm text-signal-text-tint">
              Drop to assign series
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={setPaneNode}
      data-testid={`viewport-slot-${slotId}`}
      data-series-drop-slot={slotId}
      className={`relative w-full h-full ${getBorderClass()}`}
      onMouseDownCapture={handleViewportClick}
      onMouseDown={handleMouseDown}
      onWheelCapture={handleWheelCapture}
      onContextMenu={handleContextMenu}
      onDoubleClickCapture={handleDoubleClick}
    >
      {/* Active viewport indicator — rendered as overlay to avoid parent contain:strict clipping */}
      {isActive && (
        <div className="absolute inset-0 z-30 pointer-events-none border-2 border-signal/70" />
      )}
      {findingPulse && findingPulsePoint && (
        <div
          data-testid="finding-pulse"
          aria-hidden="true"
          className="absolute z-40 pointer-events-none h-[38px] w-[38px] rounded-full border-[3px] border-signal shadow-[0_0_0_2px_rgba(255,255,255,0.7)]"
          style={{
            left: findingPulsePoint[0],
            top: findingPulsePoint[1],
            transform: "translate(-50%, -50%)",
            opacity: findingPulse.visible ? 1 : 0,
            transition: "opacity 950ms ease-out",
          }}
        />
      )}

      {newViewportFlag ? (
        // Unified viewport path (#575): the ONE render component for BOTH
        // kinds. `viewportKind` selects the internal RenderStrategy —
        // GpuVolumeStrategy ("volume", preferred) or Cpu2dStrategy ("stack",
        // fallback). Scroll/sign/AI-jump flow through the single
        // strategy-agnostic lifecycle (NavigationController + SessionGeometry),
        // so the two can't diverge (the #570 drift). MIP/MinIP blend
        // (slice_dependent layout) is NOT passed — that layout mounts
        // VolumeViewport directly via SliceDependentGrid, not this branch.
        <SessionVolumeMount
          seriesId={validatedSeriesId ?? ""}
          studyMetadata={studyMetadata}
          studyId={studyId}
          viewportKind={viewportKind}
          // Seg parity: volume always (region-grow safe on a real actor);
          // stack only the weak-GPU fallback on a geometrically-sound series
          // (stackSegEnabled — NOT blocker / is_reconstructable=false).
          // Reslice (MPR companion) panes are view + measurements only — no
          // segmentation labelmap registered on the shared volume.
          enableSegmentation={
            blindAnatomyTask || reslice ? false : viewportKind === "stack" ? stackSegEnabled : true
          }
          reslicePlane={reslice?.plane}
          windowCenter={windowing.center}
          windowWidth={windowing.width}
          externalSliceIndex={sliceIndex}
          pendingJump={pendingJump}
          viewportIdOverride={`hangingProtocol_${slotId}`}
          isInverted={isInverted}
          startAtMiddleSlice={slotConfig.plane === "Coronal" || slotConfig.plane === "Sagittal"}
          onSliceChange={handleSliceChange}
          onWindowChange={handleWindowChange}
          onCameraChange={handleCameraChange}
          // Single source of scroll direction on the unified path (#570 fix):
          // the mount computes the sign from the same SessionGeometry that
          // drives NavigationController, so scroll/scrollbar/↑↓ and the rendered
          // step can't disagree by render kind.
          onAnatomicSignChange={setListAnatomicSign}
          onLoadingStateChange={handleLoadingStateChange}
        />
      ) : viewportKind === "stack" ? (
        <StackViewport
          seriesId={validatedSeriesId ?? ""}
          studyMetadata={studyMetadata}
          studyId={studyId}
          windowCenter={windowing.center}
          windowWidth={windowing.width}
          externalSliceIndex={sliceIndex}
          pendingJump={pendingJump}
          viewportIdOverride={`hangingProtocol_${slotId}`}
          isInverted={isInverted}
          enableSegmentation={stackSegEnabled}
          onSliceChange={handleSliceChange}
          onWindowChange={handleWindowChange}
          // StackViewport predates the slot's (zoom, pan) atom pair —
          // its `onZoomChange` only carries zoom. Wire it to `setZoom` so
          // the slot's zoom-restoration round-trip works for stack panes.
          // Pan isn't currently propagated from stack viewports (no pan
          // callback on the StackViewport surface); follow-up if scout
          // workflows need pan-restore. Acceptable for v1 — scout series
          // are reviewed without intricate pan state.
          // resetCameraTrigger is intentionally not wired: this codebase
          // does camera reset imperatively via the `resetSlotView` event
          // bus handler above (line ~380), which works on any Cornerstone
          // viewport. The bus handler's volumeStatus gate is relaxed
          // below so stack panes participate.
          onZoomChange={setZoom}
          onLoadingStateChange={handleLoadingStateChange}
        />
      ) : (
        <VolumeViewport
          seriesId={validatedSeriesId ?? ""}
          studyMetadata={studyMetadata}
          studyId={studyId}
          windowCenter={windowing.center}
          windowWidth={windowing.width}
          externalSliceIndex={sliceIndex}
          viewportIdOverride={`hangingProtocol_${slotId}`}
          useGlobalSliceState={false}
          isInverted={isInverted}
          startAtMiddleSlice={slotConfig.plane === "Coronal" || slotConfig.plane === "Sagittal"}
          coordinatedResize
          onSliceChange={handleSliceChange}
          onWindowChange={handleWindowChange}
          onCameraChange={handleCameraChange}
          onLoadingStateChange={handleLoadingStateChange}
        />
      )}

      {/* Volume-load placeholder — volume only. Stack viewports load one
          image at a time and become displayable immediately. */}
      {isReconstructable && !isViewportDisplayable && (
        // pointer-events-none so wheel events fall through to the Cornerstone
        // canvas below this z-30 overlay (where the wheel handler lives —
        // SliceNavigator has none), and drag-scrubbing reaches SliceNavigator
        // (lifted to z-40 in #395 to sit above this overlay). Scrubbing during
        // load writes sliceIndex; the canvas wheel handler is a no-op until at
        // least one frame is decoded. (#395)
        <div className="absolute inset-0 z-30 pointer-events-none">
          <VolumeLoadingPlaceholder
            seriesDescription={reformatSeriesLabel(
              validatedSeriesMetadata?.series_description ?? null,
              reslice?.plane ?? null,
            )}
            loadedFrames={loadingState.loadedFrames}
            totalFrames={loadingState.totalFrames}
            slotLabel={slotConfig.label}
          />
        </div>
      )}

      {/* Frame loading progress bar — volume streaming only. */}
      {isReconstructable &&
        isViewportDisplayable &&
        loadingState.totalFrames > 0 &&
        loadingState.loadedFrames < loadingState.totalFrames && (
          <div className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none">
            <div className="h-1 bg-surface-2/50">
              <div
                className="h-full bg-signal/70 transition-all duration-300 ease-out"
                style={{
                  width: `${Math.round((loadingState.loadedFrames / loadingState.totalFrames) * 100)}%`,
                }}
              />
            </div>
            <div className="absolute bottom-1.5 right-2 text-[9px] text-fg-muted/80 tabular-nums">
              {loadingState.loadedFrames}/{loadingState.totalFrames}
            </div>
          </div>
        )}

      {/* Pending navigation indicator — volume only (stack jumps immediately). */}
      {isReconstructable &&
        pendingJump !== null &&
        !isViewportDisplayable &&
        loadingState.isLoading && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40 pointer-events-none">
            <div className="flex items-center gap-2 bg-primary/90 text-white px-4 py-2 rounded-full shadow-lg animate-pulse">
              <FaSpinner className="w-4 h-4 animate-spin" />
              <span className="text-sm font-medium">Navigating to slice {pendingJump + 1}...</span>
            </div>
          </div>
        )}

      {/* Viewport Overlay (four corners info) — shown once first frame is
          displayable. These read series metadata + camera state, not pixel
          data, so they don't need full-volume completion. */}
      {isViewportDisplayable && (
        <ViewportOverlay
          seriesInfo={seriesInfo}
          viewportMetrics={viewportMetricsData}
          isZoomMode={isZoomMode && (maximizedSlot === slotId || (!maximizedSlot && isActive))}
          phaseLabel={phaseLabel}
          modeBadge={<ViewportModeBadge meta={validatedSeriesMetadata} />}
          reformatPlane={reslice?.plane ?? null}
        />
      )}

      {/* Minimap — only on the active (hovered/clicked) viewport when zoom or pan */}
      {isViewportDisplayable && (isZoomMode || isPanMode) && isActive && (
        <ViewportMinimap
          slotId={slotId}
          zoom={zoom}
          windowCenter={windowing.center}
          windowWidth={windowing.width}
        />
      )}

      {/* Orientation Markers (A/P/L/R) — derived from the pane's real
          image_orientation_patient plus the viewport's LIVE flipHorizontal, so the
          letters always describe the pixels actually on screen. */}
      {isViewportDisplayable && <OrientationMarkers labels={orientationLabels} />}

      {/* Per-slice HU stats — volume only. Stack series have no labelmap volume. */}
      {!blindAnatomyTask && isReconstructable && isLockedViewport && !has3DViewer && (
        <SegmentationStatsOverlay
          viewportIdPrefix={`hangingProtocol_${slotId}`}
          sliceIndex={sliceIndex}
        />
      )}

      {/* Slice Navigator (vertical scrollbar) — shows as soon as the slice
          count is known from study metadata. The slot's `sliceIndex` atom is
          the single source of truth across wheel, scrollbar, ↑/↓ keys, AI
          chip jumps, and initial-load — so the thumb tracks user-intent
          immediately without consulting any cornerstone-confirmed shadow. */}
      {totalSlices > 1 && (
        <SliceNavigator
          sliceIndex={sliceIndex}
          totalSlices={totalSlices}
          onSliceChange={handleDirectSliceChange}
          isActive={isActive}
          annotatedSlices={annotatedSlices}
          segmentationSlices={seriesSegmentationMarks.map((m) => m.sliceIndex)}
          projectedSlice={projectedSlice}
          listAnatomicSign={listAnatomicSign}
        />
      )}

      {/* Bump Radius Control — segmentation brush. Renders on volume mode and
          on the weak-GPU stack fallback when seg is enabled on this pane. */}
      {(isReconstructable || stackSegEnabled) && isSegmentationActive && seriesId && (
        <BumpRadiusControl />
      )}

      {/* Drag overlay */}
      {isDragOver && (
        <div className="absolute inset-0 bg-primary/10 flex items-center justify-center pointer-events-none z-20">
          <div className="bg-bg-2/90 px-4 py-2 rounded-lg text-sm text-signal-text-tint">
            Drop to assign series
          </div>
        </div>
      )}

      {/* Right-click context menu */}
      {contextMenu && (
        <ViewportContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={closeContextMenu}
          reformat={reformat}
        />
      )}
    </div>
  );
};

export default ProtocolViewport;
